import { useState } from 'react';
import api from '../services/api';

/** `/users/bulk` → `/users` */
export function getBulkResourcePath(endpoint) {
  const path = String(endpoint || '').replace(/\/bulk\/?$/, '');
  return path.startsWith('/') ? path : `/${path}`;
}

/** Shared row selection state for list bulk update. */
export function useListBulkSelection() {
  const [selectedIds, setSelectedIds] = useState([]);
  return {
    selectedIds,
    setSelectedIds,
    clearSelection: () => setSelectedIds([]),
  };
}

export function bulkTableProps(config, currentUser, selectedIds, setSelectedIds) {
  const canSelect = Boolean(config?.canBulk?.(currentUser));
  return {
    selectable: canSelect,
    selectedIds,
    onSelectionChange: setSelectedIds,
    getRowId: (row) => row.id,
  };
}

/**
 * Apply bulk updates to local list state, show success, clear selection, then refetch.
 * @param {object} opts
 * @param {Function} opts.setItems - React setState for the list array
 * @param {Array} opts.selectedIds - ids selected at submit time (captured before clear)
 * @param {Function} opts.clearSelection
 * @param {Function} opts.setSuccess
 * @param {string} opts.successLabel - e.g. "department(s)"
 * @param {Function} [opts.refetch] - optional refetch({ forceRefresh })
 */
export function createBulkSuccessHandler({
  setItems,
  selectedIds,
  clearSelection,
  setSuccess,
  setError,
  successLabel,
  refetch,
}) {
  return (updates) => {
    const ids = [...(selectedIds || [])];
    const count = ids.length;
    const idSet = new Set(ids.map(String));

    setError?.(null);

    if (updates && Object.keys(updates).length > 0 && setItems) {
      setItems((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((row) =>
          (idSet.has(String(row.id)) ? { ...row, ...updates } : row)
        );
      });
    }

    if (setSuccess && successLabel) {
      setSuccess(`${count} ${successLabel} updated successfully.`);
    }
    clearSelection?.();
    refetch?.({ forceRefresh: true });
  };
}

/**
 * Delete selected rows via single-resource DELETE endpoints (reuses server permissions).
 */
export async function performBulkDelete(resourcePath, ids) {
  const results = await Promise.allSettled(
    ids.map((id) => api.delete(`${resourcePath}/${id}`)),
  );
  const failed = results.filter((r) => r.status === 'rejected');
  return {
    deletedCount: ids.length - failed.length,
    failed,
    firstError: failed[0]?.reason,
  };
}

export function createBulkDeleteHandler({
  resourcePath,
  setItems,
  selectedIds,
  clearSelection,
  setSuccess,
  setError,
  successLabel,
  refetch,
  filterDeleteIds,
  deleteActionPastTense = 'deleted',
}) {
  return async () => {
    let ids = [...(selectedIds || [])];
    if (filterDeleteIds) {
      const filtered = filterDeleteIds(ids);
      if (filtered === false) {
        return;
      }
      ids = filtered;
    }
    if (!ids.length) {
      setError?.('No selected items can be deleted.');
      return;
    }

    const { deletedCount, failed, firstError } = await performBulkDelete(resourcePath, ids);

    if (deletedCount > 0 && setItems) {
      const idSet = new Set(ids.map(String));
      setItems((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.filter((row) => !idSet.has(String(row.id)));
      });
    }

    if (deletedCount > 0) {
      if (failed.length === 0) {
        setError?.(null);
      }
      setSuccess?.(`${deletedCount} ${successLabel} ${deleteActionPastTense} successfully.`);
      clearSelection?.();
      refetch?.({ forceRefresh: true });
    }

    if (failed.length > 0) {
      const fallback =
        firstError?.response?.data?.message
        || Object.values(firstError?.response?.data?.errors || {}).flat()?.[0]
        || 'Delete failed.';
      setError?.(
        deletedCount > 0
          ? `${deletedCount} of ${ids.length} ${deleteActionPastTense}. ${failed.length} could not be processed. ${fallback}`
          : fallback,
      );
    }
  };
}

/** Pair bulk update + bulk delete + bulk reject handlers for ListBulkToolbar. */
export function createBulkHandlers({ endpoint, rejectEndpoint, setError, setSuccess, ...opts }) {
  const resourcePath = getBulkResourcePath(endpoint);
  const handlers = {
    onSuccess: createBulkSuccessHandler({ ...opts, setError, setSuccess }),
    onDeleteSuccess: createBulkDeleteHandler({ ...opts, resourcePath, setError, setSuccess }),
  };
  if (setError) {
    handlers.onError = (message) => {
      setSuccess?.(null);
      setError(message);
    };
  }
  if (rejectEndpoint) {
    handlers.onRejectSuccess = createBulkRejectHandler({ ...opts, rejectEndpoint, setError, setSuccess });
  }
  return handlers;
}

export function createBulkRejectHandler({
  rejectEndpoint,
  setItems,
  selectedIds,
  clearSelection,
  setSuccess,
  setError,
  successLabel,
  refetch,
  filterRejectIds,
}) {
  return async (rejectionReason) => {
    let ids = [...(selectedIds || [])];
    if (filterRejectIds) {
      ids = filterRejectIds(ids);
    }
    if (!ids.length) {
      setError?.('No selected claims can be rejected (only pending claims).');
      return;
    }
    if (!rejectionReason?.trim()) {
      setError?.('Rejection reason is required.');
      return;
    }

    try {
      const response = await api.put(rejectEndpoint, {
        ids,
        rejection_reason: rejectionReason.trim(),
      });
      const rejectedCount = response.data?.rejected_count ?? ids.length;
      const skippedCount = response.data?.skipped_count ?? 0;

      if (setItems) {
        const idSet = new Set(ids.map(String));
        setItems((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.map((row) =>
            (idSet.has(String(row.id))
              ? { ...row, status: 'rejected', is_claimable: false, is_paid: false }
              : row),
          );
        });
      }

      setError?.(null);
      if (skippedCount > 0) {
        setSuccess?.(`${rejectedCount} ${successLabel} rejected. ${skippedCount} skipped (not pending).`);
      } else {
        setSuccess?.(`${rejectedCount} ${successLabel} rejected successfully.`);
      }
      clearSelection?.();
      refetch?.({ forceRefresh: true });
    } catch (err) {
      const fallback =
        err?.response?.data?.message
        || Object.values(err?.response?.data?.errors || {}).flat()?.[0]
        || 'Reject failed.';
      setError?.(fallback);
      throw err;
    }
  };
}
